import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { BasicCard } from "../../../../components/basicCard";
import { displayModalidadeName } from "../../../../assets/content";
import { IModalidade } from "../../../../model/modalidade";
import { IStore } from "../../../../model/store";
import conveniadoService from "../../../../services/conveniadoService";
import modalidadeService from "../../../../services/modalidadeService";
import { BasicCardWrapper, GestaoConveniadosContainer } from "./styles";

const GET_CONVENIADO_ERROR_MESSAGE =
  "Ocorreu um erro ao obter os conveniados. Por favor, tente novamente.";

export const GestaoConveniados = () => {
  const params = useParams();
  const toast = useRef<Toast>(null);
  const [conveniados, setConveniados] = useState<Array<IStore>>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [size, setSize] = useState(25);
  const [page, setPage] = useState(0);
  const [first, setFirst] = useState(0);
  const [modalidade, setModalidade] = useState<string>("");
  const [modalidadesOptions, setModalidadesOptions] = useState<
    Array<IModalidade>
  >([]);

  useEffect(() => {
    const getModalidades = async () => {
      try {
        const response = await modalidadeService.getModalidade();
        const modalidades = response.data.filter((item) => item.id !== 0);
        setModalidadesOptions([
          { nome: "Todos", id: 0 },
          ...modalidades.map((item) => ({
            ...item,
            nome: displayModalidadeName(item.nome),
          })),
        ]);
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao obter modalidades.",
          life: 3000,
        });
      }
    };

    void getModalidades();
  }, []);

  function handleModalidadeChange(value: string) {
    setModalidade(value);
  }

  const getConveniados = useCallback(async () => {
    try {
      const filter = modalidade.toString() === "0" ? "" : modalidade;
      const requestParams = { filter, page, size };
      const response = await conveniadoService.getConveniadosByModalidadeId(
        requestParams
      );

      setTotalRecords(response.data.totalElements);
      setConveniados(response.data.content);
    } catch (error) {
      console.error("Erro ao obter conveniados:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: GET_CONVENIADO_ERROR_MESSAGE,
        life: 3000,
      });
    }
  }, [modalidade, page, size]);

  useEffect(() => {
    void getConveniados();
  }, [getConveniados, params.category]);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setSize(event.rows);
    setPage(event.page);
    setFirst(event.first);
    window.scrollTo(0, 350);
  };

  async function onDelete(id: number) {
    const failMessage =
      "Ocorreu um erro ao deletar o conveniado. Por favor, tente novamente mais tarde.";
    try {
      const res = await conveniadoService.deleteConveniado(id);

      if (res.status === 200) {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Conveniado deletado com sucesso!",
          life: 3000,
        });
        void getConveniados();
      } else {
        errorMessage(failMessage);
      }
    } catch (error) {
      console.error("Erro ao deletar conveniado:", error);
      errorMessage(failMessage);
    }
  }

  function errorMessage(detail: string) {
    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail,
      life: 3000,
    });
  }

  const confirmDelete = (id: number) => {
    confirmDialog({
      acceptLabel: "Confirmar",
      rejectLabel: "Cancelar",
      message: "Você tem certeza que deseja deletar esse parceiro?",
      header: "Atenção",
      icon: "pi pi-exclamation-triangle",
      accept: () => onDelete(id),
    });
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <GestaoConveniadosContainer>
        <h2 style={{ margin: "1.5rem 0" }}>Gestão de Conveniados</h2>
        <Dropdown
          value={modalidade}
          onChange={(e: DropdownChangeEvent) => handleModalidadeChange(e.value)}
          options={modalidadesOptions}
          optionLabel="nome"
          optionValue="id"
          placeholder="Selecione uma modalidade"
          className="w-full md:w-14rem"
        />

        <BasicCardWrapper>
          {Array.isArray(conveniados) &&
            conveniados.map((conveniado) => {
              const { id, nome, endereco, imagem } = conveniado;
              return (
                <BasicCard
                  image={imagem}
                  title={nome}
                  subtitle={endereco}
                  key={id}
                  onDelete={() => confirmDelete(id)}
                />
              );
            })}
        </BasicCardWrapper>
        <Paginator
          rows={size}
          first={first}
          totalRecords={totalRecords}
          rowsPerPageOptions={[25, 50, 75, 100]}
          onPageChange={onPageChange}
        />
      </GestaoConveniadosContainer>
    </>
  );
};
